<script lang="ts">
  import Icon from './Icon.svelte';
  /**
   * Fullscreen image viewer (lightbox). Shows one image at a time on a black
   * backdrop, with prev/next arrows + swipe navigation when there are multiple.
   *
   * Closes on:
   *   - tap on backdrop (anywhere outside the image)
   *   - Escape
   *   - swipe down (mobile)
   *   - the explicit × button
   *
   * We don't use the native Fullscreen API because Safari on iOS only allows
   * it on user gesture and refuses for non-video elements — a custom overlay
   * is more reliable and gives us a consistent close affordance.
   */
  import { onMount } from 'svelte';

  interface Props {
    images: string[];
    /** Which image to display first; clamped if out of range. */
    initialIndex?: number;
    onClose: () => void;
  }
  let { images, initialIndex = 0, onClose }: Props = $props();

  // svelte-ignore state_referenced_locally — знімок навмисний: лайтбокс щоразу
  // монтується заново всередині {#if}, тож нового екземпляра достатньо.
  let index = $state(
    Math.min(Math.max(0, initialIndex), Math.max(0, images.length - 1)),
  );

  function prev(): void {
    if (images.length < 2) return;
    index = (index - 1 + images.length) % images.length;
  }
  function next(): void {
    if (images.length < 2) return;
    index = (index + 1) % images.length;
  }

  // Touch handling — track start point, decide tap / horizontal swipe / vertical
  // close-swipe at touchend. 50px deadzone keeps small finger movements from
  // counting as swipes.
  let touchStartX = 0;
  let touchStartY = 0;
  let touchActive = false;
  const SWIPE_PX = 50;

  function onTouchStart(e: TouchEvent): void {
    if (e.touches.length !== 1) return;
    touchStartX = e.touches[0].clientX;
    touchStartY = e.touches[0].clientY;
    touchActive = true;
  }
  function onTouchEnd(e: TouchEvent): void {
    if (!touchActive) return;
    touchActive = false;
    const t = e.changedTouches[0];
    const dx = t.clientX - touchStartX;
    const dy = t.clientY - touchStartY;
    if (Math.abs(dx) > Math.abs(dy)) {
      if (dx > SWIPE_PX) prev();
      else if (dx < -SWIPE_PX) next();
    } else if (dy > SWIPE_PX) {
      onClose();
    }
  }

  // Keyboard navigation when the lightbox is mounted. Esc closes; arrows
  // navigate. We attach to window so focus doesn't matter — the lightbox is
  // modal anyway.
  onMount(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', onKey);
    // Lock body scroll while the lightbox is open — prevents the page behind
    // from scrolling under fingers.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  });

  // Click on the backdrop (NOT on the image) closes. Stop propagation on the
  // image so taps land cleanly.
  function onBackdropClick(): void {
    onClose();
  }

  const hasMany = $derived(images.length > 1);
</script>

<!-- svelte-ignore a11y_no_static_element_interactions a11y_click_events_have_key_events -->
<div
  class="lightbox"
  role="dialog"
  aria-modal="true"
  aria-label="Перегляд зображення"
  onclick={onBackdropClick}
  ontouchstart={onTouchStart}
  ontouchend={onTouchEnd}
>
  <button class="lb-close" onclick={(e) => { e.stopPropagation(); onClose(); }} aria-label="Закрити">
    <Icon name="x" label="Закрити" />
  </button>

  {#if hasMany}
    <button
      class="lb-arrow lb-prev"
      onclick={(e) => { e.stopPropagation(); prev(); }}
      aria-label="Попереднє"
    >‹</button>
    <button
      class="lb-arrow lb-next"
      onclick={(e) => { e.stopPropagation(); next(); }}
      aria-label="Наступне"
    >›</button>
    <div class="lb-counter">{index + 1} / {images.length}</div>
  {/if}

  <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_noninteractive_element_interactions -->
  <img
    class="lb-image"
    src={images[index]}
    alt=""
    onclick={(e) => e.stopPropagation()}
    draggable="false"
  />
</div>

<style>
  .lightbox {
    position: fixed;
    inset: 0;
    z-index: 10000;
    background: rgba(0, 0, 0, 0.95);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: env(safe-area-inset-top) env(safe-area-inset-right)
      env(safe-area-inset-bottom) env(safe-area-inset-left);
    /* user-select none so tap on backdrop doesn't accidentally select page text */
    user-select: none;
    -webkit-user-select: none;
    /* Disable touch callouts (long-press save image menu) — keeps the
     * subscriber from leaking content via context menu. */
    -webkit-touch-callout: none;
  }
  .lb-image {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    /* Prevent the image taking pointer events away from the backdrop close
     * handler if it's smaller than the viewport — but we still want clicks
     * on the image itself to NOT close, hence the stopPropagation in the
     * handler. */
    pointer-events: auto;
    -webkit-touch-callout: none;
  }
  .lb-close {
    position: absolute;
    top: max(16px, env(safe-area-inset-top));
    right: max(16px, env(safe-area-inset-right));
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: rgba(251, 246, 236, 0.12);
    border: none;
    color: var(--text-light);
    font-size: 18px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: inherit;
    transition: background 0.15s;
    z-index: 1;
  }
  .lb-close:hover {
    background: rgba(251, 246, 236, 0.22);
  }
  .lb-arrow {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 48px;
    height: 64px;
    background: rgba(251, 246, 236, 0.08);
    border: none;
    color: var(--text-light);
    font-size: 36px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: inherit;
    transition: background 0.15s;
    padding: 0;
    line-height: 0;
  }
  .lb-arrow:hover {
    background: rgba(251, 246, 236, 0.2);
  }
  .lb-prev { left: 0; border-radius: 0 12px 12px 0; }
  .lb-next { right: 0; border-radius: 12px 0 0 12px; }
  /* On phones the arrows squeeze into the side gutters; on desktop they sit a bit further in. */
  @media (min-width: 768px) {
    .lb-prev { left: 24px; }
    .lb-next { right: 24px; }
  }
  .lb-counter {
    position: absolute;
    bottom: max(20px, calc(env(safe-area-inset-bottom) + 12px));
    left: 50%;
    transform: translateX(-50%);
    color: rgba(251, 246, 236, 0.85);
    font-size: 13px;
    font-weight: 500;
    background: rgba(0, 0, 0, 0.5);
    padding: 4px 12px;
    border-radius: 999px;
  }
</style>
